import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

import { ReportsService } from 'app/API';
import { TJobReport } from 'app/types/entities/TJobReport';
import { TPaginatedResponse } from 'app/types/API/TPaginatedResponse';

import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@hooks/useAPI';
import { secondsToHHMMSS } from '@helpers/secondsToHHMMSS';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { downloadCSV } from '@helpers/downloadCSV';
import { jobIsTranscribing } from '@helpers/jobStatus/jobIsTranscribing';
import { JOB_STATUSES_ENUM } from '@constants/enums/jobStatuses';

import { CustomTable } from '@components/Table';
import { ReportFilter } from '@components/todo/ReportFilter';
import { Header } from '@components/Header';
import { StatusText } from '@components/Table/StatusText';
import { LinearProgressLoader } from '@components/spinners/LinearProgressLoader';
import { Popover } from '@components/Popover';
import { CircularProgress } from '@components/spinners/CircularProgress';
import { PagePagination } from '@components/Pagination';
import { useSearchParams } from 'react-router-dom';

const Content = styled.main`
  padding: 8px 30px 70px;
  min-height: calc(100vh - 120px);
`;
const CellContent = styled.div`
  margin: 0;
  padding: 20px 0;

  p {
    margin: 0;
  }
`;

const CenteredPercents = styled.div`
  display: flex;
  gap: 10px;
  overflow: visible !important;
`;

const StyledProgressBarWrapper = styled.div`
  display: flex;
  margin: 0;
  align-items: center;
`;

const StyledCircularProgress = styled(CircularProgress)`
  position: relative;

  .svg-pi {
    transform: rotate(-90deg); /* Fix the orientation */
    height: 20px;
    width: 20px;
  }
`;

const StyledProgressBarTextWrapper = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  padding: 0 0 0 6px;
  color: #858dbd;
`;

export const JobsReport = () => {
  const { call } = useAPI();
  const [searchParams] = useSearchParams();
  const queryPage = searchParams.get('page');
  const page = queryPage ? Number(queryPage) : 1;
  const [jobsReports, setJobsReports] = useState<TPaginatedResponse<any>>({
    data: [],
    count: 0,
  });
  const [submitTrigger, setSubmitTrigger] = useState(false);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [jobsFilter, setJobsFilter] = useState({
    from: undefined,
    to: undefined,
  });
  const { workspace, organization } = useAuthContext();

  const handleSubmit = () => {
    setSubmitTrigger((v) => !v);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getPaginatedJobs = async () => {
    if (!workspace?.id) {
      return;
    }
    setIsLoading(true);
    const fromParam = Math.floor(Number(jobsFilter?.from ?? null) / 1000) || '';
    const toParam = Math.floor(Number(jobsFilter?.to ?? null) / 1000) || '';
    const jobs = await call(
      ReportsService.getJobsReport({
        wsid: workspace?.id ?? '',
        pageNo: page.toString(),
        pageSize: pageSize.toString(),
        orderBy: 'createdAt',
        orderType: 'desc',
        search,
        jobsFrom: fromParam.toString(),
        jobsTo: toParam.toString(),
      }),
    );
    setJobsReports(jobs);
    setIsLoading(false);
  };

  useEffect(() => {
    getPaginatedJobs();
  }, [page, pageSize, jobsFilter.from, jobsFilter.to, search, submitTrigger, workspace]);

  const onExportCsv = async () => {
    const fromParam = Math.floor(Number(jobsFilter?.from ?? null) / 1000) || '';
    const toParam = Math.floor(Number(jobsFilter?.to ?? null) / 1000) || '';
    const jobsReportsForExport = await call(
      ReportsService.getJobsReport({
        wsid: workspace?.id ?? '',
        pageNo: '1',
        pageSize: jobsReports.count.toString(),
        orderBy: 'createdAt',
        orderType: 'desc',
        search,
        jobsFrom: fromParam.toString(),
        jobsTo: toParam.toString(),
      }),
    );
    const headers = [
      'ID',
      'Title',
      'Due',
      'Creator',
      'Organization',
      'Workspace',
      'Status',
      'Created',
      'Asr used',
      'Edits %',
    ];
    const array = jobsReportsForExport.data.map((data: TJobReport) => {
      return {
        id: data.id,
        title: data.jobName,
        due: data.deadline ? getAmericanTime(data.deadline) : '',
        creator: `${data.name} ${data.lastname} (${data.email})`,
        organization: organization?.name ?? '',
        workspace: workspace?.name ?? '',
        status: JOB_STATUSES_ENUM[data.status],
        created: getAmericanTime(data.createdAt),
        asr: data.duration,
        editsPercent: Math.round(data.editingProgress || 0),
      };
    });
    return downloadCSV(array, headers);
  };

  const td = [
    {
      headComponent: () => <td>Creator</td>,
      render: (data: TJobReport) => (
        <CellContent>
          <span style={{ display: 'block' }}>
            {data.name} {data.lastname}
          </span>
          <span style={{ display: 'block' }}>{data.email}</span>
        </CellContent>
      ),
    },
    {
      headComponent: () => <td>Job Title</td>,
      render: (data: TJobReport) => <CellContent>{data.jobName}</CellContent>,
    },
    {
      headComponent: () => <td>Status</td>,
      render: (data: TJobReport) => (
        <CellContent>
          {data.asOneLocked && data.status === 'JOB_STATUS_PROCESSING' ? (
            <StatusText status="Locked" />
          ) : (
            <StatusText status={JOB_STATUSES_ENUM[data.status]} />
          )}
        </CellContent>
      ),
    },
    {
      headComponent: () => <td>Created</td>,
      render: (data: TJobReport) => <CellContent>{getAmericanTime(data.createdAt)}</CellContent>,
    },
    {
      headComponent: () => <td>Asr used</td>,
      render: (data: TJobReport) => <CellContent>{secondsToHHMMSS(data.duration)}</CellContent>,
    },
    {
      headComponent: () => <td style={{ width: '1%' }}>Edits %</td>,
      render: (data: TJobReport) => {
        const percent = Math.round(data.editingProgress || 0);
        return (
          <CenteredPercents>
            {jobIsTranscribing(data.status) ? (
              <>
                <StyledProgressBarWrapper>
                  <StyledCircularProgress
                    size={20}
                    trackWidth={3}
                    indicatorWidth={3}
                    progress={percent}
                    trackColor="#CADCF8"
                    indicatorColor="#858dbd"
                  />
                  <StyledProgressBarTextWrapper>{percent}%</StyledProgressBarTextWrapper>
                </StyledProgressBarWrapper>
              </>
            ) : null}
          </CenteredPercents>
        );
      },
    },
  ];

  const FilterPopover = () => {
    return (
      <Popover
        styles="margin-top: 40px; margin-left: -100px;"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <ReportFilter
          jobsFilter={jobsFilter}
          jobsFilterOnChange={(value) => setJobsFilter(value)}
          handleSubmit={handleSubmit}
          handleClose={handleClose}
        />
      </Popover>
    );
  };

  return (
    <>
      <Helmet>
        <title>Job Reports - AutoScript</title>
      </Helmet>
      <Header
        title="Job Reports"
        setSearch={setSearch}
        search={search}
        buttonLabel={jobsReports?.data?.length ? 'Export CSV' : undefined}
        buttonOnClick={jobsReports?.data?.length ? () => onExportCsv() : undefined}
        onFilterClick={(e) => setAnchorEl(e.currentTarget)}
        filterCounter={jobsFilter.from || jobsFilter.to ? 1 : 0}
      />

      <LinearProgressLoader active={isLoading} />
      <Content>
        <CustomTable data={jobsReports?.data ?? []} head={td} />
        <PagePagination totalItemsCount={jobsReports.count ?? 0} pageSize={pageSize} setPageSize={setPageSize} />
      </Content>

      <FilterPopover />
    </>
  );
};
